/**
 * BookTripSheet — the single place to book a trip (or one leg).
 *
 * Two honest modes, no fake automation:
 *   • "Go book" — the B2 HANDOFF. Assembles the search on the carrier's own
 *     site with route, dates and passenger count carried across, and opens it.
 *     The traveller reviews and confirms THERE, in their own session.
 *   • "I already booked" — records WHICH departure they hold, the record
 *     locator, and what they PAID, so the trip page stops showing a shopping
 *     quote for a flight that is no longer being shopped.
 *
 * B2 — what the handoff can and cannot do, stated on screen rather than implied:
 *   ⚠️ The CTA may NEVER imply TripMuscle transacted. It always names the site
 *      ("Continue on aa.com"), never a bare "Book now". A traveller who believes
 *      they are booked when they are not is the worst outcome this feature can
 *      produce — worse than not shipping it.
 *   ⚠️ No carrier entry point we could find accepts a FLIGHT NUMBER. The link
 *      carries route, date(s) and party size and lands on a results list; the
 *      traveller picks the departure. The row says so and keeps the flight's
 *      details on screen to match, with Copy details as the fallback for a page
 *      that opens blank. A link that silently drops the itinerary is worse than
 *      an honest "we'll take you to AA's search".
 *   ⚠️ Nothing here scripts, drives or authenticates a session on the airline.
 *      AA Platform Terms §2(e)/§6(m)/§7(c)/§8(c) — and the AAdvantage penalty
 *      lands on the traveller's account, not ours.
 *
 * B5 — on RETURN from the handoff the sheet asks whether it got booked, because
 * the tab is still open behind the new one. Answering "yes" drops into the
 * record mode below, which is the ONLY thing that writes a booking.
 *
 * B4 — the record has to answer three questions the old version could not:
 *   1. WHICH flight. A direction is stored as several date-variant routes. On
 *      2026-08-06 Wade held AA399 on Sep 12 while the tracked focus was AA399 on
 *      Sep 13 — a different departure at $1,093 vs $775. So the record mode asks
 *      for the flight, not just the leg, and offers "it isn't in this list" for
 *      a departure we don't track rather than silently attaching the wrong row.
 *   2. WHAT the amount means. Every fare here is per person; an airline receipt
 *      is a party total. The basis is a control, not an assumption.
 *   3. Explicit CONFIRM (B5). Recording is a two-step: fill in, then review
 *      passengers / flights / fare / total and confirm. Nothing is ever recorded
 *      from a single tap.
 *
 * Opened trip-wide (all unbooked legs) from UnifiedItinerary, or scoped to one
 * leg from PlanLegSection.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink,
  Check,
  Loader2,
  Plane,
  ClipboardCopy,
  ChevronLeft,
  AlertTriangle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatTime } from "@/components/trips/itinerary/itinerary-utils";
import { bookingApi } from "@/lib/api";
import DidYouBookPrompt from "./DidYouBookPrompt";
import type { BookingHandoff, PlanFlight, PlanLeg, PlanTraveler } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  /** The legs offered for booking (already filtered to unbooked by the caller). */
  legs: PlanLeg[];
  /** Party size — the denominator for a party-total receipt (B5 review shows it). */
  passengers?: number | null;
  /** Travellers by name (R3), shown on the confirm step so you can see WHO. */
  travelers?: PlanTraveler[];
  /**
   * Which mode to open in. "record" is the MANUAL path — a traveller who booked
   * last week, or by phone, needs the same record flow without a handoff, and
   * must not have to pretend to start one to reach it.
   */
  initialMode?: "go" | "record";
}

const ROLE_LABEL: Record<PlanLeg["role"], string> = {
  outbound: "Outbound",
  return: "Return",
  extra: "Add-on",
};

const SOURCES: { value: string; label: string }[] = [
  { value: "airline_site", label: "Airline site" },
  { value: "ota", label: "Travel site" },
  { value: "other", label: "Other" },
];

/** Sentinel for "the flight I booked isn't in this list". */
const UNTRACKED = "__untracked__";

/** "$1,093" — whole dollars, matching the rest of the itinerary surface. */
function money(n: number): string {
  return "$" + Math.round(n).toLocaleString();
}

/** "AA399" — never "AA AA399". */
function flightLabel(airline: string | null, number: string | null): string | null {
  const a = (airline ?? "").trim().toUpperCase();
  const n = (number ?? "").trim().toUpperCase();
  if (a && n) return n.startsWith(a) ? n : `${a} ${n}`;
  return n || a || null;
}

/** Every flight in the direction, flattened with its date — this is the list the
 *  traveller picks from, and it spans EVERY date option, which is the whole
 *  point: the booked departure often isn't on the date currently in focus. */
function directionFlights(leg: PlanLeg): { date: string; dateLabel: string; flight: PlanFlight }[] {
  const out: { date: string; dateLabel: string; flight: PlanFlight }[] = [];
  for (const group of leg.matrix ?? []) {
    for (const f of group.flights) out.push({ date: group.date, dateLabel: group.dateLabel, flight: f });
  }
  return out;
}

/** The details you need to reproduce this leg on the airline's site, plus a
 *  copyable one-liner — because once you're on aa.com you won't recall them. */
function legFacts(leg: PlanLeg) {
  const routeCities =
    (leg.originCity || leg.origin) + " → " + (leg.destinationCity || leg.destination);
  const routeCodes = leg.origin && leg.destination ? `${leg.origin} → ${leg.destination}` : null;
  const f = leg.focus;
  const flightNo = f?.firstLegFlightNumber || f?.flightNumber || null;
  const carrier = f?.airlineName || f?.airline || null;
  const times =
    f?.departLocal && f?.arriveLocal ? `${formatTime(f.departLocal)}–${formatTime(f.arriveLocal)}` : null;
  const stops = f?.stops == null ? null : f.stops === 0 ? "nonstop" : `${f.stops} stop${f.stops > 1 ? "s" : ""}`;
  const price = f?.price != null ? `$${f.price}` : null;
  // Compact line for the clipboard (paste into notes / reference on aa.com).
  const copyLine = [
    routeCodes || routeCities,
    leg.dateLabel,
    [carrier, flightNo].filter(Boolean).join(" "),
    times,
    stops,
    price ? `seen ${price}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return { routeCities, routeCodes, flightNo, carrier, times, stops, price, copyLine };
}

/**
 * "We'll carry Dallas → Vancouver, Thu Sep 4 and 2 passengers across."
 *
 * PURE, and exported for tests — this sentence is the promise the handoff makes,
 * so it must be derived from what the SERVER says the URL actually carries, not
 * from what the UI wishes it carried. `carries.passengers === null` means the
 * URL has no passenger parameter at all (Google Flights), and the traveller
 * needs to know that before they land on a one-adult search.
 */
export function describeCarried(
  handoff: BookingHandoff,
  labels: { route: string; dateLabel: string | null; returnDateLabel?: string | null },
): string[] {
  const out = [labels.route];
  if (labels.dateLabel) {
    out.push(
      labels.returnDateLabel ? `${labels.dateLabel} → ${labels.returnDateLabel}` : labels.dateLabel,
    );
  }
  if (handoff.carries.passengers != null) {
    const n = handoff.carries.passengers;
    out.push(`${n} passenger${n === 1 ? "" : "s"}`);
  }
  return out;
}

/**
 * The honest handoff footer, shared by the per-leg row and the round-trip card.
 *
 * Three sentences, in this order, and none of them is optional:
 *   1. what we carried across,
 *   2. where you land and what you still have to do there,
 *   3. what to do if the page opens without the search filled in.
 *
 * (3) exists because we CANNOT verify the prefill end-to-end: aa.com is behind
 * Akamai and returns 403 to an ordinary request, and defeating that is the
 * §7(c) violation itself. So the UI degrades honestly instead of promising.
 */
function HandoffFacts({
  handoff,
  carried,
  copyLine,
}: {
  handoff: BookingHandoff;
  carried: string[];
  copyLine: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(copyLine);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the details are on screen anyway */
    }
  }

  return (
    <>
      <div className="mt-2 space-y-1 text-[11px] leading-relaxed text-muted-foreground">
        <div>
          <span className="font-medium text-foreground">We&apos;ll carry across:</span>{" "}
          {carried.join(" · ")}
        </div>
        <div>
          <span className="font-medium text-foreground">You&apos;ll land on:</span> {handoff.lands}
        </div>
        {/* ⚠️ Never let this read as "we selected your flight". */}
        <div>
          {handoff.carrierName} has no way to link straight to one departure, so pick it from
          their list using the details above — and if the page opens without your search filled
          in, tap <span className="font-medium">Copy details</span> and paste them in.
        </div>
      </div>
      <button
        type="button"
        onClick={copy}
        className="mt-2 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <ClipboardCopy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy details"}
      </button>
    </>
  );
}

/** One leg's HANDOFF row — the flight's specifics, an honest account of what
 *  crosses over, and a real anchor (popup-blocker-safe) to the carrier's site. */
function HandoffRow({ leg, onOpened }: { leg: PlanLeg; onOpened: (routeId: string) => void }) {
  const { data, isLoading } = useQuery({
    // Key on the focus airline too, so the link matches the flight shown.
    queryKey: ["bookingLink", leg.routeId, leg.focus?.airline ?? null],
    queryFn: () => bookingApi.getBookingLink(leg.routeId, leg.focus?.airline),
    enabled: true,
    staleTime: 5 * 60 * 1000,
  });
  const facts = legFacts(leg);

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--primary))]/[0.1] text-[hsl(var(--accent-foreground))]">
          <Plane className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold text-foreground">{facts.routeCities}</span>
            {facts.routeCodes && <span className="shrink-0 font-mono text-xs text-muted-foreground">{facts.routeCodes}</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            {ROLE_LABEL[leg.role]} · {leg.dateLabel || "date TBD"}
          </div>
          {/* The bookable specifics to search + select on the airline site. */}
          {(facts.carrier || facts.times || facts.price) && (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground">
              {(facts.carrier || facts.flightNo) && (
                <span className="font-medium">{[facts.carrier, facts.flightNo].filter(Boolean).join(" ")}</span>
              )}
              {facts.times && <span className="font-mono tabular-nums text-muted-foreground">{facts.times}</span>}
              {facts.stops && <span className="text-muted-foreground">{facts.stops}</span>}
              {facts.price && (
                <span className="tabular-nums text-muted-foreground">
                  seen <span className="font-mono">{facts.price}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-2">
        <a
          href={data?.url}
          target="_blank"
          rel="noopener noreferrer"
          // Recording the handoff must never gate opening the link — the anchor
          // navigates whether or not the write lands, and a failed write only
          // costs us the unprompted question later.
          onClick={() => onOpened(leg.routeId)}
          aria-disabled={isLoading || !data?.url}
          className={
            "inline-flex shrink-0 items-center gap-1.5 rounded-md bg-[hsl(var(--foreground))] px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 " +
            (isLoading || !data?.url ? "pointer-events-none opacity-50" : "")
          }
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
          {/* ⚠️ Always names the destination. Never a bare "Book now". */}
          {data?.cta ?? "Continue on the airline's site"}
        </a>
      </div>

      {data && (
        <HandoffFacts
          handoff={data}
          carried={describeCarried(data, { route: facts.routeCities, dateLabel: leg.dateLabel })}
          copyLine={facts.copyLine}
        />
      )}
    </div>
  );
}

export interface LegRecord {
  include: boolean;
  /** A flight id from the direction, or UNTRACKED. */
  flightId: string;
  /** Free-text identity, used only when flightId === UNTRACKED. */
  airline: string;
  flightNumber: string;
  departureDate: string;
  price: string;
  basis: "per_person" | "total";
  ref: string;
  source: string;
  /** True once the traveller edits the price, so changing flight stops re-seeding. */
  priceTouched: boolean;
}

/** One leg's row in "record" mode — which flight, what it cost, and the ref. */
function RecordRow({
  leg,
  state,
  passengers,
  onChange,
}: {
  leg: PlanLeg;
  state: LegRecord;
  passengers: number;
  onChange: (next: LegRecord) => void;
}) {
  const options = useMemo(() => directionFlights(leg), [leg]);
  const untracked = state.flightId === UNTRACKED;

  function pickFlight(id: string) {
    const hit = options.find((o) => o.flight.flightId === id);
    onChange({
      ...state,
      flightId: id,
      // Re-seed the fare from the flight they picked, unless they've typed one.
      // Picking Sep 12 after Sep 13 must not leave Sep 13's $775 in the box.
      price:
        !state.priceTouched && hit?.flight.price != null ? String(hit.flight.price) : state.price,
      basis: state.priceTouched ? state.basis : "per_person",
      // Prefill the free-text identity from the leg so the untracked path starts
      // somewhere sensible rather than blank.
      airline: state.airline || leg.focus?.airline || "",
      departureDate: state.departureDate || leg.date || "",
    });
  }

  return (
    <div className={"rounded-lg border px-3 py-2.5 " + (state.include ? "bg-card" : "bg-muted/30 opacity-70")}>
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={state.include}
          onChange={(e) => onChange({ ...state, include: e.target.checked })}
          className="h-4 w-4 accent-[hsl(var(--primary))]"
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {(leg.originCity || leg.origin) + " → " + (leg.destinationCity || leg.destination)}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">{ROLE_LABEL[leg.role]}</span>
      </label>

      {state.include && (
        <div className="mt-2 space-y-2 pl-6">
          {/* WHICH flight — across EVERY date option in this direction. */}
          <div>
            <label
              className="text-xs text-muted-foreground"
              htmlFor={"booked-flight-" + leg.routeId}
            >
              Which flight did you book?
            </label>
            <select
              id={"booked-flight-" + leg.routeId}
              value={state.flightId}
              onChange={(e) => pickFlight(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm"
            >
              {options.length === 0 && <option value={UNTRACKED}>We have no options for this leg yet</option>}
              {options.map((o) => (
                <option key={o.flight.flightId} value={o.flight.flightId}>
                  {[
                    o.dateLabel,
                    flightLabel(o.flight.airline, o.flight.firstLegFlightNumber || o.flight.flightNumber),
                    o.flight.departLocal ? formatTime(o.flight.departLocal) : null,
                    o.flight.price != null ? money(o.flight.price) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </option>
              ))}
              <option value={UNTRACKED}>It isn&apos;t in this list…</option>
            </select>
          </div>

          {/* The honest path for a departure we don't track: record what they
              tell us instead of attaching the booking to the nearest row. */}
          {untracked && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5">
              <div className="flex items-start gap-1.5 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  We&apos;ll record what you tell us, and say on the trip page that we aren&apos;t
                  tracking that exact departure yet — so the fare watch doesn&apos;t describe a
                  different flight.
                </span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[11px] text-muted-foreground">Airline</label>
                  <Input
                    value={state.airline}
                    onChange={(e) => onChange({ ...state, airline: e.target.value })}
                    className="mt-0.5"
                    placeholder="AA"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground">Flight #</label>
                  <Input
                    value={state.flightNumber}
                    onChange={(e) => onChange({ ...state, flightNumber: e.target.value })}
                    className="mt-0.5"
                    placeholder="399"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground">Departs</label>
                  <Input
                    type="date"
                    value={state.departureDate}
                    onChange={(e) => onChange({ ...state, departureDate: e.target.value })}
                    className="mt-0.5"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Price paid</label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input
                  inputMode="decimal"
                  value={state.price}
                  onChange={(e) => onChange({ ...state, price: e.target.value, priceTouched: true })}
                  className="pl-5"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Confirmation # (optional)</label>
              <Input
                value={state.ref}
                onChange={(e) => onChange({ ...state, ref: e.target.value })}
                className="mt-1"
                placeholder="e.g. ETKWRS"
                autoComplete="off"
              />
            </div>
          </div>

          {/* The unit. Our fares are per person; an airline receipt is a party
              total. Guessing between the two is how $2,398 met a $3,033 receipt. */}
          <div>
            <span className="text-xs text-muted-foreground">That amount is</span>
            <div className="mt-1 flex items-center gap-1.5">
              {(
                [
                  { value: "per_person", label: "per person" },
                  { value: "total", label: `total for ${passengers}` },
                ] as const
              ).map((b) => (
                <button
                  key={b.value}
                  type="button"
                  onClick={() => onChange({ ...state, basis: b.value })}
                  aria-pressed={state.basis === b.value}
                  className={
                    "rounded-md border px-2 py-1 text-xs transition-colors " +
                    (state.basis === b.value
                      ? "border-2 border-[hsl(var(--primary))] font-medium"
                      : "border-border text-muted-foreground")
                  }
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {SOURCES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => onChange({ ...state, source: s.value })}
                aria-pressed={state.source === s.value}
                className={
                  "rounded-md border px-2 py-1 text-xs transition-colors " +
                  (state.source === s.value
                    ? "border-2 border-[hsl(var(--primary))] font-medium"
                    : "border-border text-muted-foreground")
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** What one included leg resolves to — shared by the confirm step and the save. */
export interface ResolvedRecord {
  leg: PlanLeg;
  state: LegRecord;
  /** The picked flight, when it's one we track. */
  flight: PlanFlight | null;
  dateLabel: string | null;
  label: string | null;
  /** Parsed amount, or null when the box is empty/not a number. */
  amount: number | null;
  perPerson: number | null;
  total: number | null;
}

/** Pure: turn the form state into the numbers the confirm step displays and the
 *  API receives. Exported for unit tests — this is where the per-person/total
 *  distinction either holds or silently collapses. */
export function resolveRecords(
  legs: PlanLeg[],
  records: Record<string, LegRecord>,
  passengers: number,
): ResolvedRecord[] {
  const pax = passengers > 0 ? passengers : 1;
  return legs
    .filter((l) => records[l.routeId]?.include)
    .map((leg) => {
      const state = records[leg.routeId];
      const opt = directionFlights(leg).find((o) => o.flight.flightId === state.flightId) ?? null;
      const raw = state.price.trim();
      const parsed = raw === "" ? null : Number(raw);
      const amount = parsed != null && Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      const cents = (n: number) => Math.round(n * 100) / 100;
      return {
        leg,
        state,
        flight: opt?.flight ?? null,
        dateLabel: opt?.dateLabel ?? (state.departureDate || leg.dateLabel || null),
        label: opt
          ? flightLabel(opt.flight.airline, opt.flight.firstLegFlightNumber || opt.flight.flightNumber)
          : flightLabel(state.airline, state.flightNumber),
        amount,
        perPerson: amount == null ? null : cents(state.basis === "total" ? amount / pax : amount),
        total: amount == null ? null : cents(state.basis === "total" ? amount : amount * pax),
      };
    });
}

export default function BookTripSheet({
  open,
  onOpenChange,
  tripId,
  legs,
  passengers,
  travelers,
  initialMode = "go",
}: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"go" | "record">(initialMode);
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [error, setError] = useState<string | null>(null);
  const pax = passengers && passengers > 0 ? passengers : 1;

  /*
   * B2 → B5. Which legs we handed off in THIS sheet, and whether the traveller
   * has come back to us since.
   *
   * The handoff opens in a new tab, so our tab is still sitting here — coming
   * back fires `visibilitychange`. That is the moment to ask, and it is the
   * only cheap signal available: we cannot see aa.com and would not want to.
   *
   * ⚠️ Returning is NOT evidence of a booking. It only earns the QUESTION.
   * `handedOff` seeds nothing and writes nothing; the answer does.
   */
  const [handedOff, setHandedOff] = useState<string[]>([]);
  const [returned, setReturned] = useState(false);

  const markHandoff = useMutation({
    mutationFn: (routeIds: string[]) =>
      bookingApi.markHandoff(routeIds[0], "opened", routeIds.slice(1)),
    // Deliberately silent on failure. The durable prompt is a nicety; losing it
    // must never interrupt someone on their way to buy a ticket.
    onError: () => undefined,
  });

  function noteHandoff(routeIds: string[]) {
    setHandedOff((prev) => Array.from(new Set([...prev, ...routeIds])));
    setReturned(false);
    markHandoff.mutate(routeIds);
  }

  useEffect(() => {
    if (!open || handedOff.length === 0) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") setReturned(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [open, handedOff.length]);

  // Per-leg record state, seeded from the current focus flight so "which flight"
  // and "price paid" both start where the traveller was actually looking.
  const [records, setRecords] = useState<Record<string, LegRecord>>(() => seed(legs));
  function seed(ls: PlanLeg[]): Record<string, LegRecord> {
    const out: Record<string, LegRecord> = {};
    for (const l of ls) {
      // Only preselect the focus when it's actually in the option list — a leg
      // whose matrix hasn't been scraped yet would otherwise render a select
      // with no matching value and silently submit a flight id we can't show.
      const inMatrix =
        l.focus != null && directionFlights(l).some((o) => o.flight.flightId === l.focus!.flightId);
      out[l.routeId] = {
        include: true,
        flightId: inMatrix ? (l.focus as PlanFlight).flightId : UNTRACKED,
        airline: l.focus?.airline ?? "",
        flightNumber: l.focus?.firstLegFlightNumber ?? l.focus?.flightNumber ?? "",
        departureDate: l.date ?? "",
        price: l.focus?.price != null ? String(l.focus.price) : "",
        basis: "per_person",
        ref: "",
        source: "airline_site",
        priceTouched: false,
      };
    }
    return out;
  }

  // Re-seed whenever the sheet (re)opens with a different leg set.
  const legKey = useMemo(() => legs.map((l) => l.routeId).join(","), [legs]);
  const [seededKey, setSeededKey] = useState(legKey);
  if (open && seededKey !== legKey) {
    setRecords(seed(legs));
    setSeededKey(legKey);
    // Back to the mode the CALLER asked for, not hard-coded to "go" — otherwise
    // "Already booked?" would open on the handoff tab for anyone whose leg set
    // changed since the last open.
    setMode(initialMode);
    setStep("form");
    setHandedOff([]);
    setReturned(false);
  }

  // The caller can also flip the mode while the sheet stays mounted (the trip
  // page's "Already booked?" link on an unchanged leg set).
  const [lastInitialMode, setLastInitialMode] = useState(initialMode);
  if (open && lastInitialMode !== initialMode) {
    setLastInitialMode(initialMode);
    setMode(initialMode);
    setStep("form");
  }

  const resolved = useMemo(() => resolveRecords(legs, records, pax), [legs, records, pax]);
  const grandTotal = resolved.every((r) => r.total != null)
    ? resolved.reduce((s, r) => s + (r.total as number), 0)
    : null;

  const save = useMutation({
    mutationFn: async () => {
      if (resolved.length === 0) throw new Error("Pick at least one flight to record.");
      for (const r of resolved) {
        if (r.state.price.trim() !== "" && r.amount == null) {
          throw new Error("Price must be a number.");
        }
        await bookingApi.setStatus(r.leg.routeId, {
          status: "booked",
          booking_ref: r.state.ref.trim() || null,
          price_paid: r.amount,
          price_basis: r.state.basis,
          passengers: pax,
          flight_id: r.flight ? r.flight.flightId : null,
          booked_airline: r.flight ? r.flight.airline : r.state.airline.trim() || null,
          booked_flight_number: r.flight
            ? r.flight.firstLegFlightNumber || r.flight.flightNumber
            : r.state.flightNumber.trim() || null,
          booked_departure_date: r.flight
            ? r.flight.departDate
            : r.state.departureDate.trim() || null,
          currency: r.leg.bookingCurrency || "USD",
          source: r.state.source,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tripPlan", tripId] });
      onOpenChange(false);
    },
    onError: (e: any) => setError(e?.message || "Couldn't record the booking."),
  });

  // Round trip? Exactly one outbound + one return that mirror each other. If so,
  // offer a single round-trip search (cheaper + one PNR than two one-ways).
  const rtPair = useMemo(() => {
    if (legs.length !== 2) return null;
    const ob = legs.find((l) => l.role === "outbound");
    const rt = legs.find((l) => l.role === "return");
    if (!ob || !rt || !ob.origin || !ob.destination || !rt.origin || !rt.destination) return null;
    return ob.origin === rt.destination && ob.destination === rt.origin ? { ob, rt } : null;
  }, [legs]);
  const rtAirline = rtPair?.ob.focus?.airline ?? rtPair?.rt.focus?.airline ?? null;
  const { data: rtLink } = useQuery({
    queryKey: ["roundtripLink", rtPair?.ob.routeId, rtPair?.rt.routeId, rtAirline],
    queryFn: () => bookingApi.getRoundTripLink(rtPair!.ob.routeId, rtPair!.rt.routeId, rtAirline),
    enabled: !!rtPair,
    staleTime: 5 * 60 * 1000,
  });

  const single = legs.length === 1;
  const travelerNames = (travelers ?? []).map((t) => t.name).filter(Boolean);
  // Only ask about legs we actually handed off, and only while they are still
  // unbooked — recording one of a pair must drop it out of the question.
  const handedOffLegs = useMemo(
    () => legs.filter((l) => handedOff.includes(l.routeId) && (l.bookingStatus ?? "none") === "none"),
    [legs, handedOff],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === "confirm"
              ? "Confirm what you booked"
              : single
                ? "Book this flight"
                : "Book your trip"}
          </DialogTitle>
        </DialogHeader>

        {legs.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Every flight on this trip is already booked. 🎉
          </div>
        ) : step === "confirm" ? (
          /* ── B5: nothing is recorded silently. Passengers, flights, fare and
                 total, then an explicit confirm. ───────────────────────────── */
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              We&apos;ll record this against your trip. It doesn&apos;t book anything and moves no
              money — you&apos;ve already paid the airline.
            </p>

            <div className="rounded-lg border bg-card p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Passengers
              </div>
              <div className="mt-0.5 text-sm text-foreground">
                <span className="font-mono">{pax}</span>
                {pax === 1 ? " traveller" : " travellers"}
                {travelerNames.length > 0 && (
                  <span className="text-muted-foreground"> · {travelerNames.join(", ")}</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {resolved.map((r) => (
                <div key={r.leg.routeId} className="rounded-lg border bg-card p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-foreground">
                      {(r.leg.originCity || r.leg.origin) + " → " + (r.leg.destinationCity || r.leg.destination)}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{ROLE_LABEL[r.leg.role]}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-foreground">
                    {[r.label, r.dateLabel, r.flight?.departLocal ? formatTime(r.flight.departLocal) : null]
                      .filter(Boolean)
                      .join(" · ") || "flight not identified"}
                  </div>
                  {!r.flight && (
                    <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      We aren&apos;t tracking that exact departure yet — we&apos;ll say so on the trip page.
                    </div>
                  )}
                  <div className="mt-1.5 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {r.perPerson != null ? (
                        <>
                          <span className="font-mono">{money(r.perPerson)}</span> per person
                        </>
                      ) : (
                        "no price recorded"
                      )}
                    </span>
                    {r.total != null && (
                      <span className="font-mono tabular-nums text-foreground">{money(r.total)}</span>
                    )}
                  </div>
                  {r.state.ref.trim() && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Confirmation <span className="font-mono">{r.state.ref.trim().toUpperCase()}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between rounded-lg border-2 border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/[0.04] px-3 py-2">
              <span className="text-sm font-semibold text-foreground">Total paid</span>
              <span className="font-mono text-lg font-bold tabular-nums text-foreground">
                {grandTotal != null ? money(grandTotal) : "—"}
              </span>
            </div>
            {grandTotal == null && (
              <p className="text-xs text-muted-foreground">
                A leg with no price is still recorded as booked — we just can&apos;t compare it
                against a live fare until you add what you paid.
              </p>
            )}

            {error && <div className="text-xs text-destructive">{error}</div>}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setError(null); setStep("form"); }} disabled={save.isPending}>
                <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Back
              </Button>
              <Button className="flex-1" disabled={save.isPending} onClick={() => { setError(null); save.mutate(); }}>
                {save.isPending ? (
                  <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Recording…</>
                ) : (
                  <><Check className="mr-1.5 h-4 w-4" /> Yes, record this booking</>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Mode switch */}
            <div className="flex gap-1 rounded-lg bg-muted/50 p-1 text-sm">
              <button
                type="button"
                onClick={() => setMode("go")}
                className={"flex-1 rounded-md px-3 py-1.5 font-medium transition-colors " + (mode === "go" ? "bg-card shadow-sm" : "text-muted-foreground")}
              >
                Take me to the airline
              </button>
              <button
                type="button"
                onClick={() => setMode("record")}
                className={"flex-1 rounded-md px-3 py-1.5 font-medium transition-colors " + (mode === "record" ? "bg-card shadow-sm" : "text-muted-foreground")}
              >
                I already booked
              </button>
            </div>

            {mode === "go" ? (
              <>
                {/* B5 — the question, the moment they come back to this tab. */}
                {returned && handedOffLegs.length > 0 && (
                  <DidYouBookPrompt
                    tripId={tripId}
                    legs={handedOffLegs}
                    variant="inline"
                    onRecord={() => { setReturned(false); setMode("record"); }}
                    onAnswered={() => setReturned(false)}
                  />
                )}

                <p className="text-xs text-muted-foreground">
                  We assemble the search on the airline&apos;s own site — your route, dates and
                  party size carried across — and hand you over. You review and pay
                  <span className="font-medium"> there</span>, in your own session; TripMuscle
                  never charges your card and never signs in for you. When you come back, tell us
                  what you booked so we stop quoting a live fare for a ticket you already hold.
                </p>

                {/* Round trip → one search beats two one-ways (cheaper, one PNR). */}
                {rtPair && rtLink && (
                  <div className="rounded-lg border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/[0.04] p-3">
                    <div className="text-sm font-semibold text-foreground">Book it as one round trip</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Usually cheaper than two one-ways, and it&apos;s a single reservation.
                    </div>
                    <a
                      href={rtLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => noteHandoff([rtPair.ob.routeId, rtPair.rt.routeId])}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--foreground))] px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
                    >
                      <ExternalLink className="h-4 w-4" /> {rtLink.cta}
                    </a>
                    <HandoffFacts
                      handoff={rtLink}
                      carried={describeCarried(rtLink, {
                        route:
                          (rtPair.ob.originCity || rtPair.ob.origin) +
                          " → " +
                          (rtPair.ob.destinationCity || rtPair.ob.destination) +
                          " and back",
                        dateLabel: rtPair.ob.dateLabel,
                        returnDateLabel: rtPair.rt.dateLabel,
                      })}
                      copyLine={[legFacts(rtPair.ob).copyLine, legFacts(rtPair.rt).copyLine].join("\n")}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  {rtPair && rtLink ? (
                    <div className="text-xs font-medium text-muted-foreground">
                      Or hand off one direction at a time:
                    </div>
                  ) : (
                    legs.length > 1 && (
                      /* B3 — say it out loud rather than letting a traveller
                         think one tap covered the whole trip. */
                      <div className="text-xs text-muted-foreground">
                        These flights can&apos;t be carried across in one pass, so it&apos;s one
                        handoff per direction.
                      </div>
                    )
                  )}
                  {legs.map((l) => (
                    <HandoffRow key={l.routeId} leg={l} onOpened={(id) => noteHandoff([id])} />
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  Tell us which departure you hold and what you paid. Your trip page will then show
                  the price you committed to instead of a live quote — and we&apos;ll keep watching
                  the fare in case a drop is big enough to be worth rebooking.
                </p>
                <div className="space-y-2">
                  {legs.map((l) => (
                    <RecordRow
                      key={l.routeId}
                      leg={l}
                      passengers={pax}
                      state={
                        records[l.routeId] ?? {
                          include: true,
                          flightId: UNTRACKED,
                          airline: "",
                          flightNumber: "",
                          departureDate: "",
                          price: "",
                          basis: "per_person",
                          ref: "",
                          source: "airline_site",
                          priceTouched: false,
                        }
                      }
                      onChange={(next) => setRecords((prev) => ({ ...prev, [l.routeId]: next }))}
                    />
                  ))}
                </div>
                {error && <div className="text-xs text-destructive">{error}</div>}
                <Button
                  className="w-full"
                  disabled={resolved.length === 0}
                  onClick={() => { setError(null); setStep("confirm"); }}
                >
                  Review {resolved.length === 1 ? "this booking" : `these ${resolved.length} bookings`}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
